import React from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

/**
 * Reusable confirmation dialog component
 * Replaces browser confirm() prompts
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  type = 'warning', // 'warning' | 'danger' | 'info'
  size = 'md', // 'md' | 'lg' | 'xl' — widens the panel for richer message bodies
  confirmButtonClass,
  isLoading = false
}) => {
  if (!isOpen) return null;

  // Icon tone carries the meaning; the confirm button follows the shared button styles.
  const typeStyles = {
    warning: {
      icon: <AlertTriangle className="h-5 w-5 flex-shrink-0 text-warning-600 dark:text-warning-400" strokeWidth={1.75} aria-hidden="true" />,
      confirmBtn: 'btn-primary'
    },
    danger: {
      icon: <AlertTriangle className="h-5 w-5 flex-shrink-0 text-danger-600 dark:text-danger-400" strokeWidth={1.75} aria-hidden="true" />,
      confirmBtn: 'btn-danger'
    },
    info: {
      icon: <Info className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} aria-hidden="true" />,
      confirmBtn: 'btn-primary'
    }
  };

  const currentType = typeStyles[type] || typeStyles.warning;

  const handleConfirm = () => {
    onConfirm();
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={`w-full overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 animate-slide-up dark:bg-gray-800 dark:ring-white/10 ${
        size === 'xl' ? 'max-w-4xl' : size === 'lg' ? 'max-w-2xl' : 'max-w-md'
      }`}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <div className="flex min-w-0 items-center gap-2.5">
            {currentType.icon}
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            disabled={isLoading}
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-1">
          <div className="whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {message}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="btn btn-secondary"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={confirmButtonClass
              ? `btn text-white shadow-btn ${confirmButtonClass}`
              : `btn ${currentType.confirmBtn}`}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
